import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import './i18n';
import './App.css';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { API_URL } from './utils/api';
import BottomNav from './components/BottomNav';
import VelaroSplash from './components/VelaroSplash';

const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const RegisterPage = lazy(() => import('./pages/RegisterPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const ExplorePage = lazy(() => import('./pages/ExplorePage'));
const PublicProfilePage = lazy(() => import('./pages/PublicProfilePage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));

function VelaroLoader() {
  return (
    <div className="fixed inset-0 bg-[#050505] flex items-center justify-center z-50">
      <div className="flex flex-col items-center gap-4">
        <img src="https://customer-assets.emergentagent.com/job_velaro-concierge/artifacts/kfkog7o3_VELARO%20LOGO%20PNG.png" alt="Velaro" className="h-16 w-auto velaro-pulse-glow" />
        <span className="text-white/40 text-sm tracking-[0.3em] uppercase velaro-pulse-glow" style={{ fontFamily: 'Outfit, sans-serif' }}>VELARO</span>
      </div>
    </div>
  );
}

function PageTransitionLoader() {
  return null;
}

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) return <VelaroLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/" replace />;
  return children;
}

function GuestRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <VelaroLoader />;
  if (user) return <Navigate to={user.role === 'admin' ? '/admin' : '/'} replace />;
  return children;
}

function App() {
  const [showSplash, setShowSplash] = useState(true);

  const handleSplashDone = () => {
    setShowSplash(false);
    // Track visit
    axios.post(`${API_URL}/api/track/visit`, { page: window.location.pathname }).catch(() => {});
  };

  if (showSplash) {
    return <VelaroSplash onComplete={handleSplashDone} />;
  }

  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App min-h-screen bg-[#050505]">
          <PageTransitionLoader />
          <Suspense fallback={<VelaroLoader />}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
              <Route path="/register" element={<GuestRoute><RegisterPage /></GuestRoute>} />
              <Route path="/explore" element={<ExplorePage />} />
              <Route path="/user/:userId" element={<PublicProfilePage />} />
              <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
              <Route path="/messages" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
              <Route path="/messages/:convoId" element={<ProtectedRoute><MessagesPage /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
              <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPage /></ProtectedRoute>} />
            </Routes>
          </Suspense>
          <Toaster position="top-center" richColors />
          <BottomNav />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
